import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

const noopSetRef = () => {}

/** Fallback used when List/TabNavigation render outside the Scheduling page (e.g. unit tests). */
const DEFAULT_SCHEDULING_TOUR_REFS = {
  firstEventTypeCardRef: { current: null },
  availabilityRef: { current: null },
  isFirstEventTypeCardRefReady: false,
  isAvailabilityRefReady: false,
  setFirstEventTypeCardRef: noopSetRef,
  setAvailabilityRef: noopSetRef,
}

const SchedulingTourRefsContext = createContext(DEFAULT_SCHEDULING_TOUR_REFS)

/**
 * Shares callback refs for the Scheduling page onboarding spotlight so targets in
 * TabNavigation (Availability tab/button) and the event type list can register without
 * prop-drilling through the full scheduling tree.
 *
 * @param {{ children: React.ReactNode }} props
 */
export const SchedulingTourRefsProvider = ({ children }) => {
  const firstEventTypeCardRef = useRef(null)
  const availabilityRef = useRef(null)
  const [isFirstEventTypeCardRefReady, setFirstEventTypeCardRefReady] = useState(false)
  const [isAvailabilityRefReady, setAvailabilityRefReady] = useState(false)

  const setFirstEventTypeCardRef = useCallback((el) => {
    firstEventTypeCardRef.current = el
    setFirstEventTypeCardRefReady(!!el)
  }, [])

  // Availability is either the tab (FF off) or the manage-availability button (FF on);
  // whichever mounts calls this and clears it on unmount so shouldShow stays accurate.
  const setAvailabilityRef = useCallback((el) => {
    availabilityRef.current = el
    setAvailabilityRefReady(!!el)
  }, [])

  const value = useMemo(() => ({
    firstEventTypeCardRef,
    availabilityRef,
    isFirstEventTypeCardRefReady,
    isAvailabilityRefReady,
    setFirstEventTypeCardRef,
    setAvailabilityRef,
  }), [
    isFirstEventTypeCardRefReady,
    isAvailabilityRefReady,
    setFirstEventTypeCardRef,
    setAvailabilityRef,
  ])

  return (
    <SchedulingTourRefsContext.Provider value={value}>
      {children}
    </SchedulingTourRefsContext.Provider>
  )
}

/**
 * @returns {{
 *   firstEventTypeCardRef: React.MutableRefObject<HTMLElement|null>,
 *   availabilityRef: React.MutableRefObject<HTMLElement|null>,
 *   isFirstEventTypeCardRefReady: boolean,
 *   isAvailabilityRefReady: boolean,
 *   setFirstEventTypeCardRef: (el: HTMLElement|null) => void,
 *   setAvailabilityRef: (el: HTMLElement|null) => void,
 * }}
 */
export const useSchedulingTourRefs = () => useContext(SchedulingTourRefsContext)
